import { memo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterIcon } from '@utils/icons';
import type { useActivityFilters } from '../hooks/useActivityFilters';
import { SearchInput } from './SearchInput';
import { LinkOnlyFilterChips } from './LinkOnlyFilterChips';
import { ClearFiltersButton } from '@components/ui/ClearFiltersButton/ClearFiltersButton';
import styles from './filterBar.module.css';
import pill from './filterPill.module.css';

export interface IFilterBarProps {
  f: ReturnType<typeof useActivityFilters>;
  /** Whether the filter control-center panel is open — the trigger reflects it. */
  panelOpen: boolean;
  onTogglePanel: () => void;
  /** Search box rendered inside the bar, absorbing the free space after the
   *  trigger. Rendered only when `onSearchChange` is provided. */
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  /** Read-only context right beside the search (the Work Plan's total). */
  children?: ReactNode;
}

/**
 * The slim filter row: one "Filters" trigger (with an active-filter count)
 * that opens the control-center panel, the keyword search, and Clear. All
 * the individual filter options live in the panel (see FilterPanel) — the
 * bar carries no per-filter controls, save a chip for each filter the panel
 * can't show (a link's "critical", see `LinkOnlyFilterChips`).
 */
export const FilterBar = memo(function FilterBar({ f, panelOpen, onTogglePanel, searchValue, onSearchChange, children }: IFilterBarProps) {
  const { t } = useTranslation();
  return (
    <div className={styles.bar}>
      {/* Icon-only trigger: the tint carries the active state, the tooltip
          carries the count, Clear beside it repeats the count in text. */}
      <button
        type="button"
        className={`${pill.pill} ${pill.pillOutlined} ${pill.pillIconOnly} ${f.activeCount ? pill.pillActive : ''}`}
        aria-expanded={panelOpen}
        aria-label={t('activities.filter.filters')}
        title={f.activeCount ? `${t('activities.filter.filters')} · ${f.activeCount}` : t('activities.filter.filters')}
        onClick={onTogglePanel}
      >
        <span className={pill.icon}><FilterIcon size={15} /></span>
      </button>

      {/* Clear sits right beside the trigger so applied-filter feedback and
          its undo live in one spot. */}
      <span className={styles.clearWrap}>
        <ClearFiltersButton count={f.activeCount} onClear={f.clear} />
      </span>
      <LinkOnlyFilterChips filters={f.filters} onRemove={f.clearFlag} />

      {onSearchChange && (
        <div className={styles.searchSlot}>
          <SearchInput value={searchValue ?? ''} onChange={onSearchChange} />
        </div>
      )}
      {children}
    </div>
  );
});
